import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import { Edit as EditIcon, Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { addWeeklyDeal, updateWeeklyDeal, deleteWeeklyDeal, getWeeklyDeals, addProductToWeeklyDeal, removeProductFromWeeklyDeal } from '../../firebase/weeklyDeals';
import { getProducts } from '../../firebase/products';
import { WeeklyDeal, Product } from '../../types';
import toast from 'react-hot-toast';

const WeeklyDealsManager = () => {
  const [deals, setDeals] = useState<WeeklyDeal[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openProductDialog, setOpenProductDialog] = useState(false);
  const [editingDeal, setEditingDeal] = useState<WeeklyDeal | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<string>('');
  const [discountPercentage, setDiscountPercentage] = useState<string>('');
  const [newDeal, setNewDeal] = useState({
    title: '',
    description: '',
    type: 'discount' as WeeklyDeal['type'],
    colorScheme: 'green',
    active: true,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [dealsData, productsData] = await Promise.all([
        getWeeklyDeals(),
        getProducts(),
      ]);
      setDeals(dealsData);
      setProducts(productsData);
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (deal?: WeeklyDeal) => {
    if (deal) {
      setEditingDeal(deal);
      setNewDeal({
        title: deal.title,
        description: deal.description,
        type: deal.type,
        colorScheme: deal.colorScheme,
        active: deal.active,
      });
    } else {
      setEditingDeal(null);
      setNewDeal({
        title: '',
        description: '',
        type: 'discount',
        colorScheme: 'green',
        active: true,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingDeal(null);
    setNewDeal({
      title: '',
      description: '',
      type: 'discount',
      colorScheme: 'green',
      active: true,
    });
  };

  const handleOpenProductDialog = (deal: WeeklyDeal) => {
    setEditingDeal(deal);
    setSelectedProduct('');
    setDiscountPercentage('');
    setOpenProductDialog(true);
  };

  const handleCloseProductDialog = () => {
    setOpenProductDialog(false);
    setEditingDeal(null);
    setSelectedProduct('');
    setDiscountPercentage('');
  };

  const handleSubmit = async () => {
    try {
      if (!newDeal.title.trim() || !newDeal.description.trim()) {
        toast.error('Todos los campos son obligatorios');
        return;
      }

      if (editingDeal) {
        await updateWeeklyDeal(editingDeal.id, newDeal);
        toast.success('Oferta actualizada correctamente');
      } else {
        await addWeeklyDeal(newDeal);
        toast.success('Oferta creada correctamente');
      }

      await loadData();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving deal:', error);
      toast.error('Error al guardar la oferta');
    }
  };

  const handleAddProduct = async () => {
    if (!editingDeal || !selectedProduct || !discountPercentage) {
      toast.error('Por favor, selecciona un producto y un descuento');
      return;
    }

    const discount = parseFloat(discountPercentage);
    if (isNaN(discount) || discount <= 0 || discount >= 100) {
      toast.error('El descuento debe ser un número entre 0 y 100');
      return;
    }

    try {
      await addProductToWeeklyDeal(selectedProduct, editingDeal.id, discount);
      await loadData();
      toast.success('Producto añadido a la oferta');
      handleCloseProductDialog();
    } catch (error) {
      console.error('Error adding product to deal:', error);
      toast.error('Error al añadir el producto');
    }
  };

  const handleRemoveProduct = async (productId: string) => {
    try {
      await removeProductFromWeeklyDeal(productId);
      await loadData();
      toast.success('Producto eliminado de la oferta');
    } catch (error) {
      console.error('Error removing product from deal:', error);
      toast.error('Error al eliminar el producto');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteWeeklyDeal(id);
      await loadData();
      toast.success('Oferta eliminada correctamente');
    } catch (error) {
      console.error('Error deleting deal:', error);
      toast.error('Error al eliminar la oferta');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress sx={{ color: '#00ff00' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      bgcolor: 'black',
      border: '1px solid rgba(0, 255, 0, 0.2)',
      borderRadius: 2,
      boxShadow: '0 0 20px rgba(0, 255, 0, 0.1)',
      p: 3 
    }}>
      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3,
            bgcolor: 'rgba(255, 0, 0, 0.1)',
            border: '1px solid #ff0000',
            '& .MuiAlert-icon': {
              color: '#ff0000'
            }
          }}
        >
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ color: '#00ff00', textShadow: '0 0 10px rgba(0, 255, 0, 0.3)' }}>
          Gestionar Ofertas Semanales
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            bgcolor: '#00ff00',
            color: 'black',
            '&:hover': {
              bgcolor: '#00cc00',
            }
          }}
        >
          Nueva Oferta
        </Button>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: '#00ff00', borderBottom: '1px solid rgba(0, 255, 0, 0.2)' }}>Título</TableCell>
              <TableCell sx={{ color: '#00ff00', borderBottom: '1px solid rgba(0, 255, 0, 0.2)' }}>Descripción</TableCell>
              <TableCell sx={{ color: '#00ff00', borderBottom: '1px solid rgba(0, 255, 0, 0.2)' }}>Tipo</TableCell>
              <TableCell sx={{ color: '#00ff00', borderBottom: '1px solid rgba(0, 255, 0, 0.2)' }}>Estado</TableCell>
              <TableCell sx={{ color: '#00ff00', borderBottom: '1px solid rgba(0, 255, 0, 0.2)' }}>Productos</TableCell>
              <TableCell sx={{ color: '#00ff00', borderBottom: '1px solid rgba(0, 255, 0, 0.2)' }}>Fecha</TableCell>
              <TableCell align="right" sx={{ color: '#00ff00', borderBottom: '1px solid rgba(0, 255, 0, 0.2)' }}>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {deals.map((deal) => (
              <TableRow key={deal.id} sx={{ bgcolor: 'rgba(0, 255, 0, 0.05)' }}>
                <TableCell sx={{ color: '#00ff00', borderBottom: '1px solid rgba(0, 255, 0, 0.1)' }}>
                  {deal.title}
                </TableCell>
                <TableCell sx={{ color: '#00ffff', borderBottom: '1px solid rgba(0, 255, 0, 0.1)' }}>
                  {deal.description}
                </TableCell>
                <TableCell sx={{ color: '#00ffff', borderBottom: '1px solid rgba(0, 255, 0, 0.1)' }}>
                  {deal.type}
                </TableCell>
                <TableCell sx={{ color: '#00ffff', borderBottom: '1px solid rgba(0, 255, 0, 0.1)' }}>
                  {deal.active ? 'Activo' : 'Inactivo'}
                </TableCell>
                <TableCell sx={{ color: '#00ffff', borderBottom: '1px solid rgba(0, 255, 0, 0.1)' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleOpenProductDialog(deal)}
                    sx={{
                      color: '#00ff00',
                      borderColor: '#00ff00',
                      '&:hover': {
                        borderColor: '#00cc00',
                        bgcolor: 'rgba(0, 255, 0, 0.1)',
                      },
                    }}
                  >
                    Gestionar Productos ({deal.products?.length || 0})
                  </Button>
                </TableCell>
                <TableCell sx={{ color: '#00ffff', borderBottom: '1px solid rgba(0, 255, 0, 0.1)' }}>
                  {deal.createdAt.toLocaleDateString()}
                </TableCell>
                <TableCell align="right" sx={{ borderBottom: '1px solid rgba(0, 255, 0, 0.1)' }}>
                  <IconButton
                    size="small"
                    onClick={() => handleOpenDialog(deal)}
                    sx={{ color: '#00ff00', '&:hover': { color: '#00cc00' } }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(deal.id)}
                    sx={{ color: '#ff0000', '&:hover': { color: '#cc0000' } }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Deal Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'black',
            border: '1px solid #00ff00',
            boxShadow: '0 0 20px rgba(0, 255, 0, 0.2)',
          }
        }}
      >
        <DialogTitle sx={{ color: '#00ff00', borderBottom: '1px solid rgba(0, 255, 0, 0.2)' }}>
          {editingDeal ? 'Editar Oferta' : 'Nueva Oferta'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 2, 
            pt: 2,
            '& .MuiTextField-root, & .MuiFormControl-root': {
              '& .MuiOutlinedInput-root': {
                color: '#00ffff',
                '& fieldset': {
                  borderColor: '#00ff00',
                },
                '&:hover fieldset': {
                  borderColor: '#00ff00',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#00ff00',
                },
              },
              '& .MuiInputLabel-root': {
                color: '#00ffff',
                '&.Mui-focused': {
                  color: '#00ff00',
                },
              },
            }
          }}>
            <TextField
              fullWidth
              label="Título"
              value={newDeal.title}
              onChange={(e) => setNewDeal({ ...newDeal, title: e.target.value })}
            />

            <TextField
              fullWidth
              label="Descripción"
              value={newDeal.description}
              onChange={(e) => setNewDeal({ ...newDeal, description: e.target.value })}
            />

            <FormControl fullWidth>
              <InputLabel>Tipo</InputLabel>
              <Select
                value={newDeal.type}
                label="Tipo"
                onChange={(e) => setNewDeal({ ...newDeal, type: e.target.value as WeeklyDeal['type'] })}
              >
                <MenuItem value="discount">Descuento</MenuItem>
                <MenuItem value="twoforone">2x1</MenuItem>
                <MenuItem value="shipping">Envío</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Esquema de Color</InputLabel>
              <Select
                value={newDeal.colorScheme}
                label="Esquema de Color"
                onChange={(e) => setNewDeal({ ...newDeal, colorScheme: e.target.value })}
              >
                <MenuItem value="green">Verde</MenuItem>
                <MenuItem value="pink">Rosa</MenuItem>
                <MenuItem value="cyan">Cian</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={newDeal.active}
                  onChange={(e) => setNewDeal({ ...newDeal, active: e.target.checked })}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#00ff00',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 255, 0, 0.1)',
                      },
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#00ff00',
                    },
                  }}
                />
              }
              label={<Typography sx={{ color: '#00ffff' }}>Activo</Typography>}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid rgba(0, 255, 0, 0.2)', p: 2 }}>
          <Button 
            onClick={handleCloseDialog}
            sx={{ 
              color: '#00ffff',
              '&:hover': {
                backgroundColor: 'rgba(0, 255, 255, 0.1)',
              }
            }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            sx={{
              bgcolor: '#00ff00',
              color: 'black',
              '&:hover': {
                bgcolor: '#00cc00',
              }
            }}
          >
            {editingDeal ? 'Guardar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Product Dialog */}
      <Dialog
        open={openProductDialog}
        onClose={handleCloseProductDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'black',
            border: '1px solid #00ff00',
            boxShadow: '0 0 20px rgba(0, 255, 0, 0.2)',
          }
        }}
      >
        <DialogTitle sx={{ color: '#00ff00', borderBottom: '1px solid rgba(0, 255, 0, 0.2)' }}>
          Gestionar Productos en Oferta
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="h6" sx={{ color: '#00ff00', mb: 2 }}>
              Añadir Producto
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
              <FormControl fullWidth>
                <InputLabel>Producto</InputLabel>
                <Select
                  value={selectedProduct}
                  label="Producto"
                  onChange={(e) => setSelectedProduct(e.target.value)}
                >
                  {products
                    .filter(p => !p.weeklyDealId)
                    .map((product) => (
                      <MenuItem key={product.id} value={product.id}>
                        {product.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              <TextField
                label="Descuento (%)"
                type="number"
                value={discountPercentage}
                onChange={(e) => setDiscountPercentage(e.target.value)}
                sx={{ width: 150 }}
              />
              <Button
                variant="contained"
                onClick={handleAddProduct}
                sx={{
                  bgcolor: '#00ff00',
                  color: 'black',
                  '&:hover': {
                    bgcolor: '#00cc00',
                  }
                }}
              >
                Añadir
              </Button>
            </Box>

            <Divider sx={{ my: 3, borderColor: 'rgba(0, 255, 0, 0.2)' }} />

            <Typography variant="h6" sx={{ color: '#00ff00', mb: 2 }}>
              Productos en Oferta
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: '#00ff00' }}>Producto</TableCell>
                    <TableCell sx={{ color: '#00ff00' }}>Precio Original</TableCell>
                    <TableCell sx={{ color: '#00ff00' }}>Descuento</TableCell>
                    <TableCell sx={{ color: '#00ff00' }}>Precio Final</TableCell>
                    <TableCell align="right" sx={{ color: '#00ff00' }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {editingDeal?.products?.map((product) => (
                    <TableRow key={product.id}>
                      <TableCell sx={{ color: '#00ffff' }}>{product.name}</TableCell>
                      <TableCell sx={{ color: '#00ffff' }}>{product.price}€</TableCell>
                      <TableCell sx={{ color: '#00ffff' }}>{product.discountPercentage}%</TableCell>
                      <TableCell sx={{ color: '#00ffff' }}>
                        {(product.price * (1 - (product.discountPercentage || 0) / 100)).toFixed(2)}€
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveProduct(product.id)}
                          sx={{ color: '#ff0000' }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid rgba(0, 255, 0, 0.2)', p: 2 }}>
          <Button
            onClick={handleCloseProductDialog}
            sx={{ color: '#00ffff' }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WeeklyDealsManager;